import React from 'react';
import { Loader2, Trash2, Plus, CalendarOff } from 'lucide-react';
import { moduleApi } from '../../lib/api';

// Règles de la réservation en ligne (2026-09-23) : jusqu'ici le lien public ne connaissait que les
// horaires de la semaine — un client pouvait réserver pour dans une heure, ou pour dans trois ans, et
// personne ne pouvait fermer les réservations pendant les vacances. Ces réglages vivent à côté des
// horaires, dans Paramètres > Horaires, parce qu'ils répondent à la même question : quand peut-on venir ?

export interface BookingRules {
    min_notice_hours: number;
    max_advance_days: number;
    cancellation_enabled: boolean;
    cancellation_hours: number;
    deposit_refundable: boolean;
    cancellation_text: string;
    payment_type: 'none' | 'deposit' | 'full';
    payment_deposit_percent: number;
    payment_deadline_hours: number;
}

export const defaultBookingRules = (): BookingRules => ({
    min_notice_hours: 0,
    max_advance_days: 0,
    cancellation_enabled: false,
    cancellation_hours: 48,
    deposit_refundable: true,
    cancellation_text: '',
    payment_type: 'none',
    payment_deposit_percent: 30,
    payment_deadline_hours: 24,
});

const PAYMENT_TYPE_LABELS: Record<BookingRules['payment_type'], string> = { none: 'Aucun', deposit: 'Acompte', full: 'Intégral' };

const inputClass = 'bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

/** Bloc "règles" — piloté par le parent, qui l'enregistre avec le reste de l'onglet Horaires. */
export function BookingRulesBlock({ rules, onChange }: { rules: BookingRules; onChange: (patch: Partial<BookingRules>) => void }) {
    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Fenêtre de réservation</label>
                <p className="text-xs text-content-muted italic">S'applique au lien public et aux rendez-vous pris depuis une démarche client. Laissez à 0 pour ne rien imposer.</p>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-content-muted w-40">Pas moins de</span>
                    <input type="number" min={0} max={2160} value={rules.min_notice_hours}
                        onChange={(e) => onChange({ min_notice_hours: Math.max(0, Number(e.target.value) || 0) })}
                        className={`w-24 ${inputClass}`} />
                    <span className="text-xs text-content-muted">heures avant le rendez-vous</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-content-muted w-40">Jusqu'à</span>
                    <input type="number" min={0} max={1095} value={rules.max_advance_days}
                        onChange={(e) => onChange({ max_advance_days: Math.max(0, Number(e.target.value) || 0) })}
                        className={`w-24 ${inputClass}`} />
                    <span className="text-xs text-content-muted">jours à l'avance {rules.max_advance_days === 0 && <i>(illimité)</i>}</span>
                </div>
            </div>

            <div className="space-y-2 p-4 rounded-2xl bg-surface-soft border border-border">
                <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={rules.cancellation_enabled}
                        onChange={(e) => onChange({ cancellation_enabled: e.target.checked })} className="accent-brand-600 w-4 h-4" />
                    <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Afficher une règle d'annulation</span>
                </label>
                <p className="text-xs text-content-muted italic">Affichée au client avant qu'il ne confirme sa réservation. Elle n'annule et ne rembourse rien automatiquement : c'est l'engagement que vous affichez.</p>
                {rules.cancellation_enabled && (
                    <div className="space-y-3 pt-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-content-muted">En cas d'annulation à moins de</span>
                            <input type="number" min={0} max={2160} value={rules.cancellation_hours}
                                onChange={(e) => onChange({ cancellation_hours: Math.max(0, Number(e.target.value) || 0) })}
                                className={`w-20 ${inputClass}`} />
                            <span className="text-xs text-content-muted">heures, l'acompte est</span>
                            <div className="flex gap-2">
                                {([{ v: true, label: 'remboursé' }, { v: false, label: 'conservé' }] as const).map((opt) => (
                                    <button key={String(opt.v)} type="button" onClick={() => onChange({ deposit_refundable: opt.v })}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${rules.deposit_refundable === opt.v ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[11px] text-content-muted">Votre propre formulation (facultatif — remplace la phrase ci-dessus)</label>
                            <textarea value={rules.cancellation_text} rows={2} maxLength={500}
                                onChange={(e) => onChange({ cancellation_text: e.target.value })}
                                placeholder="Ex : Toute annulation à moins de 48 h entraîne la perte de l'acompte."
                                className={`w-full ${inputClass}`} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Paiement pour réserver — bloc autonome de Paramètres > Application (2026-10-16, refonte demandée par Emeni :
 *  « très simple à comprendre, très simple à lire »). Trois choix, un délai, un exemple chiffré. Il charge et enregistre
 *  lui-même, sans toucher aux autres règles de réservation. */
const DELAIS_PAIEMENT: Array<{ heures: number; label: string }> = [
    { heures: 1, label: '1 heure' }, { heures: 6, label: '6 heures' }, { heures: 12, label: '12 heures' },
    { heures: 24, label: '24 heures' }, { heures: 48, label: '2 jours' }, { heures: 72, label: '3 jours' },
    { heures: 0, label: 'Pas de limite' },
];

const CHOIX_PAIEMENT: Array<{ valeur: BookingRules['payment_type']; titre: string; texte: string }> = [
    { valeur: 'none', titre: 'Aucun paiement', texte: 'Le rendez-vous est confirmé tout de suite.' },
    { valeur: 'deposit', titre: 'Un acompte', texte: 'Le client paie une partie pour réserver.' },
    { valeur: 'full', titre: 'Tout', texte: 'Le client paie la totalité pour réserver.' },
];

export function BookingPaymentRuleSettings() {
    const [rules, setRules] = React.useState<BookingRules>(defaultBookingRules());
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationPlanningSettings()
            .then((s: any) => { if (s?.booking_rules) setRules({ ...defaultBookingRules(), ...s.booking_rules }); })
            .catch((e: any) => console.error('Failed to load booking payment rule', e))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true); setError('');
        try {
            // On renvoie l'ensemble des règles tel que chargé : le serveur les écrit d'un bloc.
            await moduleApi.savePrestationPlanningSettings({ booking_rules: rules });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setError(e?.message || "L'enregistrement a échoué.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>;

    const onChange = (patch: Partial<BookingRules>) => { setSaved(false); setRules((prev) => ({ ...prev, ...patch })); };
    const paiementActif = rules.payment_type !== 'none';
    const exemple = 200;
    const aPayer = rules.payment_type === 'full' ? exemple : Math.round(exemple * rules.payment_deposit_percent) / 100;
    const delaiLabel = DELAIS_PAIEMENT.find((d) => d.heures === rules.payment_deadline_hours)?.label || `${rules.payment_deadline_hours} heures`;
    const fmt = (n: number) => `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`;

    return (
        <section className="space-y-5 p-5 rounded-2xl bg-surface-soft border border-border">
            <div className="space-y-1">
                <h3 className="text-base font-bold text-content">Paiement pour réserver</h3>
                <p className="text-sm text-content-muted">Que doit payer le client pour confirmer son rendez-vous ?</p>
            </div>

            <div role="radiogroup" aria-label="Paiement pour réserver" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CHOIX_PAIEMENT.map((choix) => {
                    const actif = rules.payment_type === choix.valeur;
                    return (
                        <button key={choix.valeur} type="button" role="radio" aria-checked={actif} onClick={() => onChange({ payment_type: choix.valeur })}
                            className={`text-left p-4 rounded-xl border-2 transition-all ${actif ? 'border-brand-600 bg-surface' : 'border-border bg-surface hover:border-brand-500/50'}`}>
                            <span className="block text-sm font-bold text-content">{choix.titre}</span>
                            <span className="block text-xs text-content-muted mt-1">{choix.texte}</span>
                        </button>
                    );
                })}
            </div>

            {rules.payment_type === 'deposit' && (
                <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="acompte-pourcent" className="text-sm font-semibold text-content">Part à payer</label>
                    <input id="acompte-pourcent" type="number" min={1} max={100} value={rules.payment_deposit_percent}
                        onChange={(e) => onChange({ payment_deposit_percent: Math.min(100, Math.max(1, Number(e.target.value) || 30)) })}
                        className={`w-20 ${inputClass}`} />
                    <span className="text-sm text-content-muted">% du prix</span>
                </div>
            )}

            {paiementActif && (
                <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="delai-paiement" className="text-sm font-semibold text-content">Temps pour payer</label>
                    <select id="delai-paiement" value={rules.payment_deadline_hours}
                        onChange={(e) => onChange({ payment_deadline_hours: Number(e.target.value) })} className={inputClass}>
                        {DELAIS_PAIEMENT.map((d) => <option key={d.heures} value={d.heures}>{d.label}</option>)}
                    </select>
                    <span className="text-sm text-content-muted">Passé ce temps, le créneau est libéré.</span>
                </div>
            )}

            <p className="text-sm text-content p-3 rounded-xl bg-surface border border-border">
                {paiementActif
                    ? <>Exemple : pour une prestation à <b>{fmt(exemple)}</b>, le client paie <b>{fmt(aPayer)}</b> pour réserver{rules.payment_deadline_hours > 0 ? <> et a <b>{delaiLabel}</b> pour le faire</> : null}.</>
                    : <>Aucun paiement n'est demandé : les rendez-vous sont confirmés tout de suite.</>}
            </p>

            <p className="text-xs text-content-muted">
                Concerne la réservation en ligne et les rendez-vous créés dans le Planning. Les clients en prélèvement ne sont jamais concernés.
            </p>

            {error && <p className="text-sm text-state-error">{error}</p>}
            <button type="button" onClick={save} disabled={saving}
                className="px-5 py-2.5 rounded-xl text-sm font-bold bg-brand-600 text-brand-fg disabled:opacity-60">
                {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>
        </section>
    );
}

interface Closure { id: string; employee_id: string; employee_name: string; start_date: string; end_date: string; reason: string }

/** Bloc "fermetures" — autonome : il charge, ajoute et supprime ses périodes lui-même. */
export function BookingClosuresBlock() {
    const [closures, setClosures] = React.useState<Closure[]>([]);
    const [employees, setEmployees] = React.useState<Array<{ id: string; name: string }>>([]);
    const [loading, setLoading] = React.useState(true);
    const [adding, setAdding] = React.useState(false);
    const [error, setError] = React.useState('');
    const [draft, setDraft] = React.useState({ employee_id: '', start_date: '', end_date: '', reason: '' });

    const reload = React.useCallback(() => {
        moduleApi.getBookingClosures()
            .then((rows: any) => setClosures(Array.isArray(rows) ? rows : []))
            .catch(() => setClosures([]))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        reload();
        moduleApi.getEmployes()
            .then((rows: any) => setEmployees((Array.isArray(rows) ? rows : []).map((e: any) => ({
                id: String(e.id), name: [e.first_name, e.last_name].filter(Boolean).join(' ') || 'Salarié',
            }))))
            .catch(() => setEmployees([]));
    }, [reload]);

    const handleAdd = async () => {
        if (!draft.start_date) { setError('Choisissez au moins une date de début.'); return; }
        setAdding(true);
        setError('');
        try {
            await moduleApi.createBookingClosure({
                employee_id: draft.employee_id,
                start_date: draft.start_date,
                end_date: draft.end_date || draft.start_date,
                reason: draft.reason,
            });
            setDraft({ employee_id: '', start_date: '', end_date: '', reason: '' });
            reload();
        } catch (e: any) {
            setError(e?.message || "La période n'a pas pu être enregistrée.");
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id: string) => {
        setClosures((prev) => prev.filter((cl) => cl.id !== id));
        try { await moduleApi.deleteBookingClosure(id); } catch { reload(); }
    };

    const humanDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-2">
                    <CalendarOff size={13} /> Fermer les réservations
                </label>
                <p className="text-xs text-content-muted italic">
                    Pendant ces périodes, le lien public ne propose plus de créneau. Vous pouvez toujours ajouter un rendez-vous vous-même dans votre planning.
                    Les congés déjà posés dans le registre des absences sont pris en compte automatiquement.
                </p>
            </div>

            <div className="p-3 rounded-2xl bg-surface-soft border border-border space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <select value={draft.employee_id} onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })}
                        className={`${inputClass} flex-1 min-w-[10rem]`}>
                        <option value="">Tout l'établissement</option>
                        {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                    <input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className={inputClass} />
                    <span className="text-xs text-content-muted">au</span>
                    <input type="date" value={draft.end_date} min={draft.start_date || undefined}
                        onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className={inputClass} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input type="text" value={draft.reason} maxLength={200} placeholder="Motif (facultatif) — ex : congés d'été"
                        onChange={(e) => setDraft({ ...draft, reason: e.target.value })} className={`${inputClass} flex-1 min-w-[12rem]`} />
                    <button type="button" onClick={handleAdd} disabled={adding}
                        className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                        <Plus size={14} /> Ajouter
                    </button>
                </div>
                {error && <p className="text-xs text-red-400">{error}</p>}
            </div>

            {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
            ) : closures.length === 0 ? (
                <p className="text-xs text-content-muted italic">Aucune fermeture enregistrée.</p>
            ) : (
                <div className="space-y-2">
                    {closures.map((cl) => (
                        <div key={cl.id} className="flex items-center gap-3 p-3 rounded-xl bg-surface-soft border border-border">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-content font-bold truncate">
                                    {cl.employee_name || "Tout l'établissement"}
                                </p>
                                <p className="text-[11px] text-content-muted">
                                    {cl.start_date === cl.end_date ? humanDate(cl.start_date) : `Du ${humanDate(cl.start_date)} au ${humanDate(cl.end_date)}`}
                                    {cl.reason ? ` — ${cl.reason}` : ''}
                                </p>
                            </div>
                            <button type="button" onClick={() => handleDelete(cl.id)} title="Supprimer cette fermeture"
                                className="p-2 rounded-lg text-content-muted hover:text-red-400 hover:bg-red-500/10">
                                <Trash2 size={15} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
